import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useApp } from '@/hooks/use-app-context';
import { useToast } from '@/hooks/use-toast';
import { Icon } from '@/lib/icons';
import { ExcelPhraseForm } from './ExcelPhraseForm';
import { ExcelPhraseList } from './ExcelPhraseList';
import { ExcelPhraseToolbar } from './ExcelPhraseToolbar';
import { excelPhraseErrorMessage } from './excel-phrase-errors';
import { EMPTY_PHRASE_FORM, type ExcelPhraseFormValue } from './excel-phrase.types';
import { useExcelFilePhrases } from './use-excel-file-phrases';
import { PhraseRecordingSummary } from './PhraseRecordingSummary';
import { applyRecordingSort, statsIndex, type RecordingSort } from './phrase-recording-stats';
import { usePhraseRecordingStats } from './use-phrase-recording-stats';

interface Props { fileId: number; fileName: string; open: boolean; onOpenChange: (open: boolean) => void; }
const PAGE_SIZE = 50;

export function ExcelFilePhrasesDialog({ fileId, fileName, open, onOpenChange }: Props) {
  const { t } = useApp();
  const copy = t.admin.excelPhrases;
  const { toast } = useToast();
  const phrasesState = useExcelFilePhrases(fileId, open);
  const [form, setForm] = useState<ExcelPhraseFormValue>(EMPTY_PHRASE_FORM);
  const [search, setSearch] = useState('');
  const [domain, setDomain] = useState('');
  const [page, setPage] = useState(1);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [isConfirming, setIsConfirming] = useState(false);
  const [recordingSort, setRecordingSort] = useState<RecordingSort>('file');
  const statsQuery = usePhraseRecordingStats(fileId, open);
  const statsOf = useMemo(() => statsIndex(statsQuery.data ?? []), [statsQuery.data]);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_PHRASE_FORM);
    setSearch(''); setDomain(''); setPage(1);
    setSelectedKeys(new Set()); setIsConfirming(false); setRecordingSort('file');
  }, [open]);

  const domains = useMemo(() => [...new Set(phrasesState.phrases.map((row) => row.domain).filter(Boolean))].sort(), [phrasesState.phrases]);
  const filtered = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    const matching = phrasesState.phrases.filter((row) => (!domain || row.domain === domain)
      && (!query || row.local.toLocaleLowerCase().includes(query) || row.fr.toLocaleLowerCase().includes(query)));
    return applyRecordingSort(matching, recordingSort, (row) => statsOf(row)?.recordings ?? 0);
  }, [domain, phrasesState.phrases, search, recordingSort, statsOf]);
  const neverRecorded = useMemo(
    () => (statsQuery.data ? phrasesState.phrases.filter((row) => !statsOf(row)?.recordings).length : null),
    [phrasesState.phrases, statsOf, statsQuery.data],
  );
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visible = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const selected = phrasesState.phrases.filter((row) => selectedKeys.has(row.key));

  const reloadAndClear = async () => {
    setSelectedKeys(new Set()); setIsConfirming(false);
    await phrasesState.reload();
  };
  const addPhrase = async () => {
    try {
      const response = await phrasesState.addPhrase(form);
      if (response.message !== 'EXCEL_PHRASE_ADDED') return;
      toast({ title: t.success, description: copy.added });
      setForm(EMPTY_PHRASE_FORM); await reloadAndClear(); setPage(Number.MAX_SAFE_INTEGER);
    } catch (error) { toast({ title: t.error, description: excelPhraseErrorMessage(error, copy.serverErrors), variant: 'destructive' }); }
  };
  const removeSelected = async () => {
    try {
      const response = await phrasesState.removePhrases(selected);
      if (response.message !== 'EXCEL_PHRASES_REMOVED') return;
      toast({ title: t.success, description: copy.removedMany.replace('{count}', String(response.data.removed)) });
      await reloadAndClear();
    } catch (error) { toast({ title: t.error, description: excelPhraseErrorMessage(error, copy.serverErrors), variant: 'destructive' }); }
  };
  const importFile = async (file: File) => {
    try {
      const response = await phrasesState.importPhrases(file);
      if (response.message !== 'EXCEL_PHRASES_IMPORTED') return;
      const description = copy.imported.replace('{added}', String(response.data.added)).replace('{skipped}', String(response.data.skipped));
      toast({ title: t.success, description }); await reloadAndClear();
    } catch (error) { toast({ title: t.error, description: excelPhraseErrorMessage(error, copy.serverErrors), variant: 'destructive' }); }
  };
  const toggle = (key: string) => setSelectedKeys((current) => {
    const next = new Set(current);
    if (next.has(key)) next.delete(key);
    else if (next.size < 200) next.add(key);
    else toast({ title: t.error, description: copy.selectionLimit, variant: 'destructive' });
    return next;
  });
  const toggleAll = () => setSelectedKeys((current) => {
    const next = new Set(current);
    const shouldClear = visible.every((row) => next.has(row.key));
    visible.forEach((row) => {
      if (shouldClear) next.delete(row.key);
      else if (next.size < 200) next.add(row.key);
    });
    if (!shouldClear && next.size === 200 && visible.some((row) => !current.has(row.key))) {
      toast({ title: t.error, description: copy.selectionLimit, variant: 'destructive' });
    }
    return next;
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] w-[calc(100%-2rem)] max-w-5xl flex-col gap-0 overflow-hidden rounded-2xl border border-border/50 bg-background p-0 shadow-2xl sm:w-full">
        <DialogHeader className="border-b border-border/50 bg-background px-6 py-5 text-left">
          <DialogTitle className="truncate pr-6">{copy.title.replace('{file}', fileName)}</DialogTitle>
          <DialogDescription className="leading-6">{copy.description}</DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-6">
          <ExcelPhraseForm copy={copy} value={form} isSubmitting={phrasesState.isAdding} onChange={setForm} onSubmit={() => void addPhrase()} />
          <ExcelPhraseToolbar copy={copy} search={search} domain={domain} domains={domains} selectedCount={selected.length} isConfirming={isConfirming} isRemoving={phrasesState.isRemoving} isImporting={phrasesState.isImporting} onSearchChange={(value) => { setSearch(value); setPage(1); }} onDomainChange={(value) => { setDomain(value); setPage(1); }} recordingSort={recordingSort} onRecordingSortChange={(value) => { setRecordingSort(value); setPage(1); }} onImport={(file) => void importFile(file)} onAskRemove={() => setIsConfirming(true)} onCancelRemove={() => setIsConfirming(false)} onRemove={() => void removeSelected()} />
          {phrasesState.isLoading && <p className="flex items-center gap-2 py-6 text-sm text-muted-foreground"><Icon name="loader2" className="h-4 w-4 animate-spin" />{copy.loading}</p>}
          {phrasesState.isError && <p className="py-6 text-sm text-destructive">{copy.error} <button type="button" onClick={() => void phrasesState.reload()} className="font-semibold underline">{copy.retry}</button></p>}
          {!phrasesState.isLoading && !phrasesState.isError && filtered.length === 0 && <p className="py-6 text-sm text-muted-foreground">{phrasesState.phrases.length === 0 ? copy.empty : copy.noMatch}</p>}
          {neverRecorded !== null && phrasesState.phrases.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {copy.neverRecordedSummary.replace('{count}', String(neverRecorded)).replace('{total}', String(phrasesState.phrases.length))}
            </p>
          )}
          {statsQuery.isError && <p className="text-xs text-muted-foreground">{copy.recordingsUnavailable}</p>}
          {visible.length > 0 && (
            <ExcelPhraseList
              copy={copy}
              rows={visible}
              selectedKeys={selectedKeys}
              onToggle={toggle}
              onToggleAll={toggleAll}
              renderStats={statsQuery.isError ? undefined : (row) => <PhraseRecordingSummary copy={copy} stats={statsOf(row)} isLoading={statsQuery.isLoading} />}
            />
          )}
        </div>
        {!phrasesState.isLoading && !phrasesState.isError && <div className="flex items-center justify-between border-t border-border/50 bg-background px-6 py-3 text-xs"><span className="text-muted-foreground">{copy.count.replace('{count}', String(filtered.length))}</span><div className="flex items-center gap-4"><button type="button" disabled={currentPage <= 1} onClick={() => setPage(currentPage - 1)} className="font-semibold disabled:opacity-35">{copy.previous}</button><span className="tabular-nums text-muted-foreground">{copy.pageSummary.replace('{page}', String(currentPage)).replace('{pages}', String(totalPages))}</span><button type="button" disabled={currentPage >= totalPages} onClick={() => setPage(currentPage + 1)} className="font-semibold disabled:opacity-35">{copy.next}</button></div></div>}
      </DialogContent>
    </Dialog>
  );
}
